import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { achatsService } from "@/api/services";
import { DataTable, type Column } from "@/components/common/DataTable";
import { EmptyState } from "@/components/common/EmptyState";
import { ErrorState } from "@/components/common/ErrorState";
import { LoadingState } from "@/components/common/LoadingState";
import { Modal } from "@/components/common/Modal";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import {
  construireCommandeFournisseur,
  fournisseursCommuns,
} from "@/features/achats/commandeFournisseur";
import {
  construireReception,
  creerCleReception,
  executerReception,
} from "@/features/achats/receptionFournisseur";
import { useI18n } from "@/i18n/I18nProvider";
import { useAuth } from "@/auth/AuthProvider";
import type {
  BesoinAchat,
  CommandeFournisseur,
  CommandeFournisseurDetail,
} from "@/types/domain";

export default function AchatsPage() {
  const { t } = useI18n();
  const { hasAnyPermission } = useAuth();
  const peutGererAchats = hasAnyPermission("procurement.manage");
  const peutReceptionner = hasAnyPermission("warehouse.receive");
  const [besoins, setBesoins] = useState<BesoinAchat[]>([]);
  const [commandes, setCommandes] = useState<CommandeFournisseur[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selection, setSelection] = useState<string[]>([]);
  const [fournisseurId, setFournisseurId] = useState("");
  const [numeroCommande, setNumeroCommande] = useState("");
  const [creation, setCreation] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [detail, setDetail] = useState<CommandeFournisseurDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [ligneReceptionId, setLigneReceptionId] = useState<string | null>(null);
  const [quantiteReception, setQuantiteReception] = useState("");
  const [numeroReception, setNumeroReception] = useState("");
  const [cleReception, setCleReception] = useState("");
  const [receptionEnCours, setReceptionEnCours] = useState(false);
  const [receptionMessage, setReceptionMessage] = useState<string | null>(null);

  const charger = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [data, commandesData] = await Promise.all([
        achatsService.besoins(),
        achatsService.commandesFournisseur(),
      ]);
      setBesoins(Array.isArray(data) ? data : []);
      setCommandes(Array.isArray(commandesData) ? commandesData : []);
    } catch (err) {
      console.error("Erreur chargement besoins d'achat :", err);
      setError("Impossible de charger les besoins d’achat.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    charger();
  }, [charger]);

  const besoinsSelectionnes = besoins.filter((besoin) =>
    selection.includes(besoin.id),
  );
  const fournisseursDisponibles = fournisseursCommuns(besoinsSelectionnes);

  const basculerBesoin = (besoinId: string) => {
    setMessage(null);
    setFournisseurId("");
    setSelection((courante) =>
      courante.includes(besoinId)
        ? courante.filter((id) => id !== besoinId)
        : [...courante, besoinId],
    );
  };

  const creerCommande = async () => {
    try {
      setCreation(true);
      setMessage(null);
      const payload = construireCommandeFournisseur(
        besoinsSelectionnes,
        fournisseurId,
        numeroCommande,
      );
      const resultat = await achatsService.creerCommandeFournisseur(payload);
      setMessage(
        resultat.idempotent_replay
          ? `Commande ${resultat.numero_commande_fournisseur} déjà enregistrée; résultat existant réutilisé.`
          : `Commande ${resultat.numero_commande_fournisseur} créée avec ${resultat.lignes.length} ligne(s).`,
      );
      setSelection([]);
      setFournisseurId("");
      setNumeroCommande("");
      await charger();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Création impossible.");
    } finally {
      setCreation(false);
    }
  };

  const consulterCommande = async (commandeId: string) => {
    try {
      setDetailLoading(true);
      setMessage(null);
      setDetail(await achatsService.commandeFournisseurDetail(commandeId));
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Détail impossible à charger.");
    } finally {
      setDetailLoading(false);
    }
  };

  const ouvrirReception = (
    ligne: CommandeFournisseurDetail["lignes"][number],
  ) => {
    setLigneReceptionId(ligne.id);
    setQuantiteReception(String(ligne.quantite_restante));
    setNumeroReception("");
    setCleReception(creerCleReception(ligne.id));
    setReceptionMessage(null);
  };

  const fermerReception = () => {
    if (receptionEnCours) return;
    setLigneReceptionId(null);
    setQuantiteReception("");
    setNumeroReception("");
    setCleReception("");
    setReceptionMessage(null);
  };

  const enregistrerReception = async () => {
    if (!detail || !ligneReceptionId) return;
    const ligne = detail.lignes.find((item) => item.id === ligneReceptionId);
    if (!ligne) {
      setReceptionMessage("La ligne sélectionnée n’existe plus.");
      return;
    }
    try {
      setReceptionMessage(null);
      const payload = construireReception(
        ligne,
        Number(quantiteReception),
        cleReception,
        numeroReception,
      );
      const resultat = await executerReception({
        commandeId: detail.id,
        ligneId: ligne.id,
        payload,
        recevoir: achatsService.recevoirLigneFournisseur,
        rafraichir: async () => {
          const nouveauDetail = await achatsService.commandeFournisseurDetail(detail.id);
          setDetail(nouveauDetail);
          await charger();
        },
        verrouiller: setReceptionEnCours,
      });
      setReceptionMessage(
        resultat.idempotent_replay
          ? "Réception déjà enregistrée; le résultat existant a été réutilisé."
          : `Réception enregistrée. Stock physique: ${resultat.stock_physique}.`,
      );
      setLigneReceptionId(null);
      setQuantiteReception("");
      setNumeroReception("");
      setCleReception("");
    } catch (err) {
      setReceptionMessage(
        err instanceof Error ? err.message : "Réception impossible.",
      );
    }
  };

  const columns: Column<BesoinAchat>[] = [
    {
      key: "selection",
      header: "Sélection",
      render: (besoin) => (
        <input
          type="checkbox"
          aria-label={`Sélectionner le besoin ${besoin.produit.sku}`}
          checked={selection.includes(besoin.id)}
          disabled={!peutGererAchats || besoin.statut !== "A_COMMANDER"}
          onChange={() => basculerBesoin(besoin.id)}
          className="h-4 w-4 rounded border-border"
        />
      ),
    },
    {
      key: "commande",
      header: "Commande source",
      render: (besoin) => (
        <Link
          to={`/commandes/${besoin.commande.id}`}
          className="ops-focus rounded-sm font-mono font-semibold hover:underline"
        >
          {besoin.commande.numero_commande}
        </Link>
      ),
    },
    { key: "sku", header: "SKU", mono: true, render: (besoin) => besoin.produit.sku },
    { key: "description", header: "Produit", render: (besoin) => besoin.produit.description },
    {
      key: "quantite_a_acheter",
      header: "À acheter",
      align: "right",
      mono: true,
      render: (besoin) => besoin.quantite_a_acheter,
    },
    {
      key: "fournisseur",
      header: "Fournisseur",
      render: (besoin) => besoin.fournisseur?.nom ?? "À déterminer",
    },
    {
      key: "statut",
      header: "Statut",
      render: (besoin) => <StatusBadge status={besoin.statut} />,
    },
  ];

  const commandesColumns: Column<CommandeFournisseur>[] = [
    {
      key: "numero",
      header: "Commande fournisseur",
      mono: true,
      render: (commande) => (
        <button
          type="button"
          onClick={() => consulterCommande(commande.id)}
          disabled={detailLoading}
          className="ops-focus rounded-sm font-mono font-semibold hover:underline disabled:opacity-50"
        >
          {commande.numero_commande_fournisseur}
        </button>
      ),
    },
    {
      key: "fournisseur",
      header: "Fournisseur",
      render: (commande) => commande.fournisseur.nom,
    },
    {
      key: "lignes",
      header: "Besoins liés",
      align: "right",
      mono: true,
      render: (commande) => commande.nombre_lignes,
    },
    {
      key: "quantite",
      header: "Quantité",
      align: "right",
      mono: true,
      render: (commande) => commande.quantite_commandee,
    },
    {
      key: "statut",
      header: "Statut",
      render: (commande) => <StatusBadge status={commande.statut} />,
    },
    {
      key: "date",
      header: "Créée le",
      render: (commande) => new Date(commande.date_commande).toLocaleString(),
    },
  ];

  return (
    <div className="space-y-6" data-testid="achats-page">
      <PageHeader
        title={t.modules.achats.title}
        subtitle="Transformer les manques confirmés en commandes fournisseur traçables."
        breadcrumb={[t.appSuffix, t.modules.achats.title]}
      />
      <section className="ops-panel">
        <div className="border-b border-border px-4 py-3">
          <h2 className="text-sm font-semibold tracking-tight">Besoins d’achat</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            PostgreSQL est la source autoritative; aucun fournisseur n’est présélectionné.
          </p>
        </div>
        {peutGererAchats && selection.length > 0 && (
          <div className="grid gap-3 border-b border-border bg-muted/30 p-4 md:grid-cols-[1fr_1fr_auto] md:items-end">
            <label className="text-xs font-medium">
              Fournisseur commun ({selection.length} besoin(s))
              <select
                value={fournisseurId}
                onChange={(event) => setFournisseurId(event.target.value)}
                className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">Sélection explicite requise</option>
                {fournisseursDisponibles.map((fournisseur) => (
                  <option key={fournisseur.id} value={fournisseur.id}>
                    {fournisseur.nom}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs font-medium">
              Numéro de commande fournisseur
              <input
                value={numeroCommande}
                onChange={(event) => setNumeroCommande(event.target.value)}
                maxLength={100}
                placeholder="Ex. PO-2026-0042"
                className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 font-mono text-sm"
              />
            </label>
            <button
              type="button"
              onClick={creerCommande}
              disabled={creation || !fournisseurId || !numeroCommande.trim()}
              className="ops-focus h-9 rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            >
              {creation ? "Création..." : "Créer la commande"}
            </button>
            {fournisseursDisponibles.length === 0 && (
              <p className="text-xs text-destructive md:col-span-3">
                Aucun fournisseur actif n’est compatible avec toute la sélection. Séparez ces besoins.
              </p>
            )}
          </div>
        )}
        {message && (
          <p className="border-b border-border px-4 py-2 text-sm" role="status">
            {message}
          </p>
        )}
        {loading ? (
          <LoadingState label="Chargement des besoins d’achat..." rows={6} />
        ) : error ? (
          <ErrorState title="Chargement impossible" description={error} onRetry={charger} />
        ) : besoins.length === 0 ? (
          <EmptyState
            title="Aucun besoin d’achat."
            description="Les réservations entièrement couvertes ne créent aucun besoin."
          />
        ) : (
          <DataTable
            columns={columns}
            rows={besoins}
            rowKey={(besoin) => besoin.id}
            testId="besoins-achat-table"
          />
        )}
      </section>
      <section className="ops-panel">
        <div className="border-b border-border px-4 py-3">
          <h2 className="text-sm font-semibold tracking-tight">Commandes fournisseur</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Une identité stable regroupe uniquement des besoins compatibles avec le même fournisseur.
          </p>
        </div>
        {loading ? (
          <LoadingState label="Chargement des commandes fournisseur..." rows={4} />
        ) : commandes.length === 0 ? (
          <EmptyState
            title="Aucune commande fournisseur."
            description="Sélectionnez un ou plusieurs besoins à commander ci-dessus."
          />
        ) : (
          <DataTable
            columns={commandesColumns}
            rows={commandes}
            rowKey={(commande) => commande.id}
            testId="commandes-fournisseur-table"
          />
        )}
      </section>
      <Modal
        open={detail !== null}
        onOpenChange={(open) => {
          if (!open && !receptionEnCours) {
            setDetail(null);
            fermerReception();
          }
        }}
        title={detail?.numero_commande_fournisseur ?? "Commande fournisseur"}
        description={detail ? `${detail.fournisseur.nom} · ${detail.statut.replace(/_/g, " ")}` : undefined}
        className="max-w-4xl"
        testId="commande-fournisseur-detail"
      >
        {detail && (
          <div className="space-y-4">
            <div className="grid gap-2 text-sm sm:grid-cols-3">
              <div><span className="text-muted-foreground">Créée le</span><br />{new Date(detail.date_commande).toLocaleString()}</div>
              <div><span className="text-muted-foreground">Lignes</span><br />{detail.lignes.length}</div>
              <div><span className="text-muted-foreground">Commentaire</span><br />{detail.commentaire ?? "—"}</div>
            </div>
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-left text-sm">
                <thead className="bg-muted/50 text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">Commande client</th>
                    <th className="px-3 py-2">Produit</th>
                    <th className="px-3 py-2">Réf. fournisseur</th>
                    <th className="px-3 py-2 text-right">Commandé</th>
                    <th className="px-3 py-2 text-right">Reçu</th>
                    <th className="px-3 py-2 text-right">Restant</th>
                    <th className="px-3 py-2">Statut</th>
                    <th className="px-3 py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.lignes.map((ligne) => (
                    <tr key={ligne.id} className="border-t border-border">
                      <td className="px-3 py-2">
                        <Link className="ops-focus rounded-sm font-mono hover:underline" to={`/commandes/${ligne.commande_client.id}`} onClick={() => setDetail(null)}>
                          {ligne.commande_client.numero_commande}
                        </Link>
                      </td>
                      <td className="px-3 py-2"><span className="font-mono">{ligne.produit.sku}</span><br /><span className="text-xs text-muted-foreground">{ligne.produit.description}</span></td>
                      <td className="px-3 py-2 font-mono">{ligne.reference_fournisseur ?? "—"}</td>
                      <td className="px-3 py-2 text-right font-mono">{ligne.quantite_commandee}</td>
                      <td className="px-3 py-2 text-right font-mono">{ligne.quantite_recue}</td>
                      <td className="px-3 py-2 text-right font-mono">{ligne.quantite_restante}</td>
                      <td className="px-3 py-2"><StatusBadge status={ligne.statut} /></td>
                      <td className="px-3 py-2">
                        {peutReceptionner && <button
                          type="button"
                          onClick={() => ouvrirReception(ligne)}
                          disabled={receptionEnCours || ligne.quantite_restante <= 0 || ligne.statut === "RECU"}
                          className="ops-focus rounded-md border border-border px-2 py-1 text-xs font-semibold hover:bg-muted disabled:opacity-50"
                        >
                          Réceptionner
                        </button>}
                        {ligne.receptions.length > 0 && (
                          <div className="mt-1 text-xs text-muted-foreground">
                            {ligne.receptions.length} réception(s)
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {ligneReceptionId && (() => {
              const ligne = detail.lignes.find((item) => item.id === ligneReceptionId);
              if (!ligne) return null;
              return (
                <div className="rounded-md border border-border bg-muted/20 p-4" data-testid="reception-fournisseur-form">
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold">Réception — {ligne.produit.sku}</h3>
                      <p className="text-xs text-muted-foreground">
                        Commandé {ligne.quantite_commandee} · reçu {ligne.quantite_recue} · restant {ligne.quantite_restante}
                      </p>
                    </div>
                    <button type="button" onClick={fermerReception} disabled={receptionEnCours} className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-50">
                      Annuler
                    </button>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
                    <label className="text-xs font-medium">
                      Quantité reçue
                      <input
                        type="number"
                        min={1}
                        step={1}
                        max={ligne.quantite_restante}
                        value={quantiteReception}
                        onChange={(event) => setQuantiteReception(event.target.value)}
                        disabled={receptionEnCours}
                        className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 font-mono text-sm disabled:opacity-50"
                      />
                    </label>
                    <label className="text-xs font-medium">
                      Numéro de réception (optionnel)
                      <input
                        value={numeroReception}
                        maxLength={100}
                        onChange={(event) => setNumeroReception(event.target.value)}
                        disabled={receptionEnCours}
                        className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 font-mono text-sm disabled:opacity-50"
                      />
                    </label>
                    <button
                      type="button"
                      onClick={enregistrerReception}
                      disabled={receptionEnCours || quantiteReception === ""}
                      className="ops-focus h-9 rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-50"
                    >
                      {receptionEnCours ? "Réception..." : "Confirmer"}
                    </button>
                  </div>
                </div>
              );
            })()}
            {receptionMessage && (
              <p className="text-sm" role="status">{receptionMessage}</p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
